import React from "react";
import { useTranslation } from "react-i18next";

/**
 * The drawn night map behind the login form: three routes into the school,
 * three buses with their labels. Shown while the 3D town loads, and instead of
 * it where there is no WebGL, reduced motion is asked for, or the scene fails.
 */

const ROADS = [
  "M-40 170 L940 120",
  "M-40 455 L940 500",
  "M-40 740 L940 690",
  "M150 -40 L215 940",
  "M470 -40 L440 940",
  "M760 -40 L800 940",
];
const STREETS = ["M-40 300 L940 320", "M-40 600 L940 590", "M310 -40 L330 940", "M610 -40 L620 940"];
const HOMES = [
  [90, 230], [240, 240], [390, 225], [540, 215], [700, 400], [860, 410],
  [100, 540], [250, 520], [560, 540], [700, 560], [860, 620], [90, 820],
  [390, 820], [540, 800], [700, 810], [860, 800], [240, 660], [540, 660],
];
const ROUTES = [
  { d: "M820 140 C 760 210, 700 230, 640 300 S 520 380, 455 478", color: "#26B6D0", from: [820, 140] },
  { d: "M80 250 C 180 260, 230 330, 300 380 S 400 470, 455 478", color: "#F3B141", from: [80, 250] },
  { d: "M250 860 C 300 760, 330 700, 380 640 S 440 540, 455 478", color: "#D1255E", from: [250, 860] },
];
const BUSES = [
  { n: 2, at: [640, 300], color: "#26B6D0", status: "way" },
  { n: 4, at: [300, 380], color: "#F19A47", status: "arrived" },
  { n: 3, at: [380, 640], color: "#D1255E", status: "way" },
];
const DOT = { way: "#26B6D0", arrived: "#2fae6b" };

function LoginMapFallback({ className }) {
  const { t } = useTranslation();
  return (
    <svg
      className={className}
      viewBox="0 0 900 900"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
    >
      <g fill="none" stroke="#19243d" strokeWidth="18" strokeLinecap="round">
        {ROADS.map((d) => (
          <path key={d} d={d} />
        ))}
      </g>
      <g fill="none" stroke="#151e35" strokeWidth="8" strokeLinecap="round">
        {STREETS.map((d) => (
          <path key={d} d={d} />
        ))}
      </g>
      <g fill="#2a3756">
        {HOMES.map(([x, y]) => (
          <circle key={`${x}-${y}`} cx={x} cy={y} r="4" />
        ))}
      </g>
      <g fill="none" strokeLinecap="round" strokeLinejoin="round">
        {ROUTES.map((r) => (
          <path key={`glow-${r.d}`} d={r.d} stroke={r.color} strokeWidth="16" opacity="0.15" />
        ))}
        {ROUTES.map((r) => (
          <path key={r.d} d={r.d} stroke={r.color} strokeWidth="5" />
        ))}
      </g>
      {ROUTES.map((r) => (
        <circle key={`from-${r.d}`} cx={r.from[0]} cy={r.from[1]} r="6" fill="#0B1222" stroke={r.color} strokeWidth="3" />
      ))}
      <rect x="436" y="459" width="38" height="38" rx="11" fill="#FFFFFF" />
      <path
        d="M445 489 V478 L455 470 L465 478 V489 Z M452 489 V482 H458 V489"
        fill="none"
        stroke="#1F2E4B"
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
      {BUSES.map((b) => (
        <g key={b.n} transform={`translate(${b.at[0]} ${b.at[1]})`}>
          <circle r="20" fill={b.color} opacity="0.22" />
          <circle r="9" fill={b.color} stroke="#FFFFFF" strokeWidth="3" />
          <rect x="-72" y="-80" width="144" height="52" rx="14" fill="#FFFFFF" />
          <path d="M-8 -29 L0 -20 L8 -29 Z" fill="#FFFFFF" />
          <text
            x="0"
            y="-56"
            textAnchor="middle"
            fill="#1F2E4B"
            style={{ fontFamily: "Cairo, sans-serif", fontSize: 15, fontWeight: 700 }}
          >
            {t("auth.login.scene.bus", { n: b.n })}
          </text>
          <g transform="translate(0 -37)">
            <circle cx="-44" cy="-4" r="4" fill={DOT[b.status]} />
            <text
              x="4"
              y="0"
              textAnchor="middle"
              fill="#5a6782"
              style={{ fontFamily: "Cairo, sans-serif", fontSize: 12.5, fontWeight: 600 }}
            >
              {t(`auth.login.scene.status.${b.status}`)}
            </text>
          </g>
        </g>
      ))}
    </svg>
  );
}

export default LoginMapFallback;
